import { useCallback, useEffect, useState } from "react";
import { generatePath, useHistory, useParams } from "react-router-dom";
import { getList } from "../../api/generics";
import { SUCCESS } from "../../utils/constants/tags";
import { successAlert, warningAlert } from "../utils/messages";
import { getRoutePath } from "../../routes";
import {
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CDataTable,
  CPagination,
  CRow,
  CTooltip,
} from "@coreui/react";
import { BsFillPlusCircleFill } from "react-icons/bs";
import UserTaxPayer from "../../models/user-tax-payer";
import UserTaxPayerUpdateModal from "./UserTaxPayerUpdateModal";

const ITEMS_PER_PAGE = 20;

const UserTaxPayerCrud = () => {
  const fields = [
    {
      key: "user",
      _classes: ["text-center"],
      label: "Usuario",
    },
    {
      key: "isSuperUser",
      _classes: ["text-center"],
      label: "Super Usuario",
    },
    {
      key: "actions",
      _classes: ["text-center"],
      label: "Acciones",
      filter: false,
    },
  ];

  const { id: taxPayerId } = useParams<{ id?: string }>();

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<UserTaxPayer[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState<UserTaxPayer | undefined>(
    undefined
  );

  const history = useHistory();

  const getURLParams = () => {
    let urlParams = new URLSearchParams(history.location.search);
    let page = urlParams.get("page")
      ? parseInt(urlParams.get("page") as string)
      : 1;

    if (page <= 0) {
      page = 1;
    }

    return { page: page };
  };

  const fetchItems = useCallback(async () => {
    const urlParams = getURLParams();
    const limit = ITEMS_PER_PAGE;
    const offset = ITEMS_PER_PAGE * Number(urlParams.page - 1);
    const itemsRequestStatus = await getList<UserTaxPayer>(
      `/tax_payers/${taxPayerId}/users/`,
      limit,
      offset
    );

    if (itemsRequestStatus.status === SUCCESS) {
      if (itemsRequestStatus.data !== undefined) {
        const count = itemsRequestStatus.data.count;
        const pages = Math.ceil((count ? count : 0) / ITEMS_PER_PAGE);
        setTotalPages(pages);
        setItems(itemsRequestStatus.data.items);
        setCurrentPage(urlParams.page);
      }
    } else {
      const message = itemsRequestStatus.detail
        ? itemsRequestStatus.detail
        : "Error desconocido";
      warningAlert(message);
    }

    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxPayerId]);

  const onPageChange = (page: number) => {
    setLoading(true);
    let urlParams = new URLSearchParams(history.location.search);
    urlParams.set("page", page.toString());
    let url = `?${urlParams.toString()}`;
    history.push(url);
  };

  const onAddClick = () => {
    setEditingItem(undefined);
    setShowEditModal(true);
  };

  const onUpdateClick = (item: UserTaxPayer) => {
    setEditingItem(item);
    setShowEditModal(true);
  };

  const onEditCancel = () => {
    setShowEditModal(false);
  };

  const onEditSuccess = () => {
    successAlert("Datos guardados con éxito!");
    setShowEditModal(false);
    setItems([]);
    setLoading(true);
    //setTotalPages(1);
    //setCurrentPage(1);
    //reset();
    fetchItems();
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return history.listen((location) => {
      if (
        location.pathname !==
        generatePath(getRoutePath("userTaxPayers"), {
          id: taxPayerId,
        })
      ) {
        return;
      }
      fetchItems();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  return (
    <>
      <CRow>
        <CCol lg="12">
          <CCard>
            <CCardHeader className="d-flex flex-row mb-3">
              <div className="p-2 ">
                <h3>Usuarios de Contribuyente</h3>
              </div>
              <div className="p-2 ">
                <CButton
                  color="primary"
                  className="float-right"
                  onClick={onAddClick}
                >
                  <BsFillPlusCircleFill />
                </CButton>
              </div>
            </CCardHeader>
            <CCardBody>
              <div>
                <CDataTable
                  noItemsView={<h2 className="text-center">Sin Resultados</h2>}
                  addTableClasses={"table-fixed"}
                  fields={fields}
                  items={items}
                  striped
                  border
                  loading={loading}
                  responsive
                  scopedSlots={{
                    user: (item: UserTaxPayer) => {
                      return (
                        <td className="text-center">{item.user?.email}</td>
                      );
                    },
                    isSuperUser: (item: UserTaxPayer) => {
                      return (
                        <td className="text-center">
                          {item.isSuperUser === true ? "Sí" : "No"}
                        </td>
                      );
                    },
                    actions: (item: UserTaxPayer) => {
                      const editButton = (
                        <CTooltip content="Editar">
                          <CButton
                            className="text-white"
                            color="warning"
                            onClick={() => {
                              onUpdateClick(item);
                            }}
                          >
                            <i className="fa fa-pencil"></i>
                          </CButton>
                        </CTooltip>
                      );

                      return (
                        <td className="text-center">
                          <CButtonGroup>{editButton}</CButtonGroup>
                        </td>
                      );
                    },
                  }}
                />
                <div className="d-flex justify-content-center">
                  <CPagination
                    pages={totalPages}
                    activePage={currentPage}
                    onActivePageChange={(i: number) => onPageChange(i)}
                    className={totalPages < 2 ? "d-none" : ""}
                  />
                </div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <UserTaxPayerUpdateModal
        show={showEditModal}
        onCancel={onEditCancel}
        onSuccess={onEditSuccess}
        userTaxPayer={editingItem}
        taxPayerId={
          taxPayerId !== undefined && !isNaN(parseInt(taxPayerId))
            ? parseInt(taxPayerId)
            : undefined
        }
      />
    </>
  );
};

export default UserTaxPayerCrud;
